import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import api from '../../../api'
import { useAppSelector } from '../../../store'
import { IntTplState, setAllPageData, setPages } from '../../../store/internationalTplSlice'
import { getInternationalTplPages } from './pagesConfig'
import { InternationalTplPageSlug } from '../../../domain/types'

const PAGE_SLUGS: InternationalTplPageSlug[] = [
  'international-tpl-which-country-to-drive',
  'international-tpl-insurance-period',
  'international-tpl-personal-info',
  'international-tpl-car-info',
  'international-tpl-offer',
  'international-tpl-check-info',
]

export const useInternationalTplPages = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { pages, pageData } = useAppSelector((data: { internationalTpl: IntTplState }) => data.internationalTpl)

  // Fetch all page data using RTK Query hooks (must be called unconditionally)
  const page1 = api.useGetIntTplPageInfoQuery('international-tpl-which-country-to-drive')
  const page2 = api.useGetIntTplPageInfoQuery('international-tpl-insurance-period')
  const page3 = api.useGetIntTplPageInfoQuery('international-tpl-personal-info')
  const page4 = api.useGetIntTplPageInfoQuery('international-tpl-car-info')
  const page5 = api.useGetIntTplPageInfoQuery('international-tpl-offer')
  const page6 = api.useGetIntTplPageInfoQuery('international-tpl-check-info')

  // Extract data and loading values for dependency tracking
  const page1Data = page1.data
  const page2Data = page2.data
  const page3Data = page3.data
  const page4Data = page4.data
  const page5Data = page5.data
  const page6Data = page6.data

  const page1Loading = page1.isLoading
  const page2Loading = page2.isLoading
  const page3Loading = page3.isLoading
  const page4Loading = page4.isLoading
  const page5Loading = page5.isLoading
  const page6Loading = page6.isLoading

  // Check if all queries are loaded (not loading and have data or error)
  const allLoaded = useMemo(
    () =>
      !page1Loading &&
      !page2Loading &&
      !page3Loading &&
      !page4Loading &&
      !page5Loading &&
      !page6Loading &&
      (page1Data || page1.error) &&
      (page2Data || page2.error) &&
      (page3Data || page3.error) &&
      (page4Data || page4.error) &&
      (page5Data || page5.error) &&
      (page6Data || page6.error),
    [
      page1Loading,
      page2Loading,
      page3Loading,
      page4Loading,
      page5Loading,
      page6Loading,
      page1Data,
      page2Data,
      page3Data,
      page4Data,
      page5Data,
      page6Data,
      page1.error,
      page2.error,
      page3.error,
      page4.error,
      page5.error,
      page6.error,
    ],
  )

  // Get page configs
  const pageConfigs = getInternationalTplPages(t)

  // Store pages in Redux when they're loaded
  useEffect(() => {
    if (pages.length === 0 && pageConfigs.length > 0) {
      dispatch(setPages(pageConfigs))
    }
  }, [pages.length, pageConfigs, dispatch])

  // Store all page data in Redux when loaded
  useEffect(() => {
    if (allLoaded && Object.keys(pageData).length === 0) {
      const dataMap: Record<string, any> = {}
      const allData = [page1Data, page2Data, page3Data, page4Data, page5Data, page6Data]
      allData.forEach((data, index) => {
        if (data && PAGE_SLUGS[index]) {
          dataMap[PAGE_SLUGS[index]] = data
        }
      })
      if (Object.keys(dataMap).length > 0) {
        dispatch(setAllPageData(dataMap))
      }
    }
  }, [allLoaded, pageData, dispatch, page1Data, page2Data, page3Data, page4Data, page5Data, page6Data])

  return {
    pages: pages.length > 0 ? pages : pageConfigs,
    pageData,
    isLoading: !allLoaded,
  }
}
